import { IEventsPlugin } from '@/shared/Charting/LineChart/lineChart.types.ts'
import LineChart from '@/shared/Charting/LineChart'
import { IEquipment, IEquipmentEvent } from '@/pages/Equipment/equipment.types.ts'
import equipmentApi from '@/pages/Equipment/equipment.api.ts'
import eventBus from '@/services/eventBus'
import equipmentUtils from '@/pages/Equipment/equipment.utils.ts'
import canvasUtils from '@/shared/Charting/canvas-utils'
import formatUtils from '@/utils/format.utils.ts'

const PLUGIN_HEGIHT = 36

const LineChartEventsPlugin: (equipment: IEquipment) => IEventsPlugin = (equipment) => ({
  _loading: false,
  _fetchRequest: null,
  _computedClusters: [],
  _lastMouseEvent: null,
  _displayingEventTooltip: false,
  chart: null,
  events: [],
  init(chart: LineChart) {
    this.chart = chart

    chart.setPluginInnerPaddings('events-plugin', { left: 0, right: 0, top: 0, bottom: PLUGIN_HEGIHT })

    // В случае, если график находится в мануальном режиме (не Live, то запрашиваем список событий на изменение отображаемого интервала)
    this.chart.zoom.changes$.subscribe(() => {
      if (this.chart.zoom.mode === 'manual') {
        this._loadEvents()
      }
    })

    // В случае если график в лайв режиме, слушаем обновления, иначе отписываемся
    let eventSubscription: Function
    this.chart.zoom.mode$.subscribe((mode) => {
      if (mode === 'auto') {
        eventSubscription = this._subscribeToNewEvents()
      } else if (eventSubscription && typeof eventSubscription === 'function') {
        eventSubscription()
      }
    })

    if (this.chart.zoom.mode === 'auto') {
      this._subscribeToNewEvents()
    }
  },
  async _subscribeToNewEvents() {
    await this._fetchRequest

    return eventBus.sub('equipment.updated', (ev) => {
      if (ev.id !== equipment.id || ev.statusFlag === 'NORMAL') return

      const lastStatusFlag = this.events.length ? this.events.at(-1).payload.flag : equipment.statusFlag
      const lastStatusDescription = this.events.length
        ? this.events.at(-1).payload.errorDescription
        : equipment.errorDescription

      if (lastStatusFlag !== ev.statusFlag || ev.errorDescription !== lastStatusDescription) {
        this.events.push({
          id: crypto ? crypto.randomUUID() : (Math.random() * Math.random()).toFixed(10),
          createdAt: ev.updatedAt,
          type: 'LP_ERROR',
          equipmentId: ev.id,
          payload: { errorDescription: ev.errorDescription, errorCode: ev.errorCode, flag: ev.statusFlag },
        })
      }
    })
  },
  _loadEvents() {
    this._loading = true
    equipmentApi.getEquipmentEventsAbortable.abort()

    this._fetchRequest = equipmentApi
      .getEquipmentEventsAbortable({
        limit: 200,
        created_gte: new Date(this.chart.zoom.xMin).toISOString(),
        created_lte: new Date(this.chart.zoom.xMax).toISOString(),
        equipmentId: equipment.id,
      })
      .then((r) => {
        if (r.status === 'success') {
          this.events = r.body.items
        }
      })
      .finally(() => {
        this._loading = false
      })
  },
  render(ctx: CanvasRenderingContext2D) {
    const bottomOy = ctx.canvas.height - this.chart.paddings.bottom

    ctx.clearRect(
      this.chart.paddings.left,
      bottomOy - PLUGIN_HEGIHT,
      this.chart.canvas.width - this.chart.paddings.left - this.chart.paddings.right,
      PLUGIN_HEGIHT
    )

    // Проводим линию и филим ректангл согласно дизайну
    ctx.fillStyle = '#00000005'
    ctx.fillRect(
      this.chart.paddings.left,
      bottomOy - PLUGIN_HEGIHT,
      this.chart.canvas.width - this.chart.paddings.left - this.chart.paddings.right,
      PLUGIN_HEGIHT
    )

    ctx.beginPath()
    ctx.strokeStyle = '#0000001A'
    ctx.lineWidth = 1
    ctx.moveTo(this.chart.paddings.left, bottomOy - PLUGIN_HEGIHT)
    ctx.lineTo(ctx.canvas.width - this.chart.paddings.right, bottomOy - PLUGIN_HEGIHT)
    ctx.stroke()
    ctx.closePath()

    // Рендерим ивенты
    if (!this.events.length) {
      this._computedClusters = []
      return
    }
    const clusters = this._groupEventsIntoCluster(
      this.events,
      this.chart.zoom.xMin,
      this.chart.zoom.xMax,
      this.chart.canvas.width - this.chart.paddings.left - this.chart.paddings.right
    )
    this._computedClusters = clusters

    const pointOy = bottomOy - PLUGIN_HEGIHT / 2
    for (const cluster of clusters) {
      // Отображаем либо точку, либо кластер с количеством событий в нем
      if (cluster.events.length === 1) {
        ctx.beginPath()

        ctx.arc(cluster.centerPx, pointOy, 6, 0, 360)
        ctx.fillStyle = equipmentUtils.getStatusFlagColor(cluster.events[0].payload.flag)
        ctx.fill()
        ctx.closePath()
      } else {
        ctx.beginPath()
        ctx.arc(cluster.centerPx, pointOy, 10, 0, 360)
        ctx.fillStyle = equipmentUtils.getStatusFlagColor(cluster.events[0].payload.flag)
        ctx.fill()
        ctx.closePath()

        ctx.beginPath()
        ctx.fillStyle = '#fff'
        ctx.fillText(cluster.events.length.toString(), cluster.centerPx - 4, pointOy + 4, 20)
        ctx.fill()
        ctx.closePath()
      }
    }
  },
  onAfterRender(ctx: CanvasRenderingContext2D) {
    if (!this._lastMouseEvent) return

    const e = this._lastMouseEvent
    const bottomOy = this.chart.ctx.canvas.height - this.chart.paddings.bottom
    const pointOy = bottomOy - PLUGIN_HEGIHT / 2

    const rect = ctx.canvas.getBoundingClientRect()
    const mouse = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    const { x, y } = mouse

    let foundTooltipToDisplay = false
    for (const ev of this._computedClusters) {
      if (ev.startPx <= x && ev.endPx >= x && y >= pointOy - 6 && y <= pointOy + 6) {
        foundTooltipToDisplay = true

        let text = ev.events[0].payload.errorDescription
        if (ev.events.length > 1) {
          text = `${ev.events.length} ${formatUtils.numWord(ev.events.length, ['событие', 'события', 'событий'])}`
        }
        canvasUtils.tooltip.drawTooltip(ctx, text, ev.centerPx, pointOy - 8)
      }
    }

    this._displayingEventTooltip = foundTooltipToDisplay
  },
  onMouseMove(e) {
    this._lastMouseEvent = e
  },

  legendInterceptor() {
    return {
      id: 'events-plugin',
      kind: 'prevent-render',
      interceptor: () => {
        return !this._displayingEventTooltip
      },
    }
  },
  /**
   * Groups events into clusters if their 8px representations would overlap on a timeline.
   * - Events outside [tsFrom, tsTo] are ignored (not rendered).
   * - Overlap is computed in pixel space using eventWidthPx centered on each event.
   * - Each output cluster contains at least one event.
   *
   * @param events           Array of equipment events
   * @param tsFrom           Timeline start (UNIX ms)
   * @param tsTo             Timeline end (UNIX ms)
   * @param timelineWidthPx  Width of the timeline in pixels
   * @param eventWidthPx     Visual width of a single event in pixels (default 8)
   */
  _groupEventsIntoCluster(
    events: IEquipmentEvent[],
    tsFrom: number,
    tsTo: number,
    timelineWidthPx: number,
    eventWidthPx: number = 12
  ) {
    if (tsTo <= tsFrom) return []
    if (timelineWidthPx <= 0) return []

    const half = eventWidthPx / 2
    const span = tsTo - tsFrom

    // Convert to seconds since epoch from createdAt and compute pixel center
    type WithGeom = IEquipmentEvent & {
      _ts: number // seconds
      _cx: number // center x in px
      _left: number // left edge in px
      _right: number // right edge in px
    }

    const withGeom: WithGeom[] = events
      .map((e) => {
        const ts = new Date(e.createdAt).getTime()
        const cx = ((ts - tsFrom) / span) * timelineWidthPx
        return {
          ...e,
          _ts: ts,
          _cx: cx,
          _left: cx - half,
          _right: cx + half,
        }
      })
      // keep only items that intersect the visible timeline horizontally at all
      // (i.e., at least some part of their 8px falls within [0, timelineWidthPx])
      .filter((e) => e._right >= 0 && e._left <= timelineWidthPx)
      // sort by time (or by center px; they’re equivalent monotonic transforms here)
      .sort((a, b) => a._ts - b._ts || a.id.localeCompare(b.id))

    const clusters: ILineChartEventsPluginCluster[] = []
    if (withGeom.length === 0) return clusters

    // Start the first cluster
    let curEvents: WithGeom[] = [withGeom[0]]
    let curLeft = withGeom[0]._left
    let curRight = withGeom[0]._right

    for (let i = 1; i < withGeom.length; i++) {
      const e = withGeom[i]
      const overlaps = e._left <= curRight // inclusive overlap/adjacency

      if (overlaps) {
        // Grow current cluster
        curEvents.push(e)
        if (e._left < curLeft) curLeft = e._left
        if (e._right > curRight) curRight = e._right
      } else {
        // Flush current cluster
        clusters.push(buildCluster(curEvents, curLeft, curRight))
        // Start a new one
        curEvents = [e]
        curLeft = e._left
        curRight = e._right
      }
    }

    // Flush the last cluster
    clusters.push(buildCluster(curEvents, curLeft, curRight))

    return clusters

    function buildCluster(items: WithGeom[], leftPx: number, rightPx: number): ILineChartEventsPluginCluster {
      // Derive center as mean of centers (you could also use midpoint of [left,right])
      const centerPx = items.reduce((acc, e) => acc + e._cx, 0) / items.length

      const startTs = Math.min(...items.map((e) => e._ts))
      const endTs = Math.max(...items.map((e) => e._ts))

      return {
        events: items.map(({ _ts, _cx, _left, _right, ...orig }) => orig),
        startPx: clamp(leftPx, 0, timelineWidthPx),
        endPx: clamp(rightPx, 0, timelineWidthPx),
        centerPx: clamp(centerPx, 0, timelineWidthPx),
        startTs,
        endTs,
      }
    }

    function clamp(v: number, min: number, max: number) {
      return Math.max(min, Math.min(max, v))
    }
  },
})

export interface ILineChartEventsPluginCluster {
  // Events included in this cluster (original order preserved by time)
  events: IEquipmentEvent[]

  // Derived geometry for convenience
  startPx: number // leftmost pixel (inclusive) spanned by the cluster
  endPx: number // rightmost pixel (inclusive) spanned by the cluster
  centerPx: number // average of member centers, can be useful for labels

  // Time range covered by the cluster (seconds since epoch)
  startTs: number
  endTs: number
}

export default LineChartEventsPlugin
